/**
 * AddNotesModal
 * @module components/AddNotesModal
 */

import React, { useContext, useState } from 'react';
import { LocalizationContext } from 'context/localization.context';
import Modal from 'components/ui/Modal';
import Button from '../Button';
import { useSelector } from 'react-redux';
import { selectCartObject } from 'store/modules/cart';

interface Props {
	/** Toggle modal callback */
	onToggle: any;

	handleAddNotes: any;

	goodsMarking?: string;

	comment?: string;

	commentHidden?: boolean;

	maxLengthLimits?: {
		articleGoodsMarkingLength: number;
		articleCommentsLength: number;
	};
}
const AddNotesModal = ({
	onToggle,
	handleAddNotes,
	goodsMarking: defaultGoodMarking,
	comment: defaultComment,
	commentHidden,
	maxLengthLimits,
}: Props) => {
	const { t }: any = useContext(LocalizationContext);
	const { goodsMarkingHidden } = useSelector(selectCartObject);
	const [goodsmarking, setGoodsmarking] = useState(defaultGoodMarking || '');
	const [comment, setComment] = useState(defaultComment || '');

	const [
		showGoodsmarkingEmojiWarning,
		setShowGoodsmarkingEmojiWarning,
	] = useState(false);
	const [showCommentEmojiWarning, setShowCommentEmojiWarning] = useState(false);

	const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

	const handleGoodsmarkingChange = (e: any) => {
		const { value } = e.target;
		emojiRegex.lastIndex = 0;
		if (emojiRegex.test(value)) {
			setGoodsmarking(value.replace(emojiRegex, ''));
			setShowGoodsmarkingEmojiWarning(true);
		} else {
			setGoodsmarking(value);
			setShowGoodsmarkingEmojiWarning(false);
		}
	};

	const handleCommentChange = (e: any) => {
		const { value } = e.target;
		emojiRegex.lastIndex = 0;
		if (emojiRegex.test(value)) {
			setComment(value.replace(emojiRegex, ''));
			setShowCommentEmojiWarning(true);
		} else {
			setComment(value);
			setShowCommentEmojiWarning(false);
		}
	};

	const handleSave = () => {
		emojiRegex.lastIndex = 0;
		const goodsmarkingHasEmoji = emojiRegex.test(goodsmarking);
		emojiRegex.lastIndex = 0;
		const commentHasEmoji = emojiRegex.test(comment);

		if (goodsmarkingHasEmoji || commentHasEmoji) {
			setShowGoodsmarkingEmojiWarning(goodsmarkingHasEmoji);
			setShowCommentEmojiWarning(commentHasEmoji);
			return;
		}

		handleAddNotes(goodsmarking, comment);
	};

	return (
		<Modal
			toggleModal={onToggle}
			heading={t('shared/articlerow/addanote')}
			title="Add notes modal"
			width="small"
		>
			{!goodsMarkingHidden ? (
				<div className="mb-5">
					<label className="block" htmlFor="goodsmarking">
						{t('shared/articlerow/goodsmarking')}
					</label>
					<textarea
						className="w-full h-25 p-2 border border-greyDark rounded-lg"
						id="goodsmarking"
						value={goodsmarking}
						onChange={handleGoodsmarkingChange}
						maxLength={maxLengthLimits?.articleGoodsMarkingLength}
					></textarea>
					{showGoodsmarkingEmojiWarning && (
						<p className="text-orange text-sm px-2">
							{t('shared/articlerow/emojinotallowed')}
						</p>
					)}
				</div>
			) : (
				<></>
			)}

			{commentHidden ? (
				<></>
			) : (
				<div className="mb-5">
					<label className="block" htmlFor="comment">
						{t('shared/articlerow/comment')}
					</label>
					<textarea
						className="w-full h-25 p-2 border border-greyDark rounded-lg"
						id="comment"
						value={comment}
						onChange={handleCommentChange}
						maxLength={maxLengthLimits?.articleCommentsLength}
					/>
					{showCommentEmojiWarning && (
						<p className="text-orange text-sm px-2">
							{t('shared/articlerow/emojinotallowed')}
						</p>
					)}
				</div>
			)}

			<div className="flex justify-end">
				<Button
					className="block m-0"
					type="button"
					buttonColor="blue"
					onClick={handleSave}
				>
					{t('shared/articlerow/savenote')}
				</Button>
			</div>
		</Modal>
	);
};

export default AddNotesModal;
